/**
 * MoonDisc — precision SVG moon that mathematically renders the exact
 * `illumination` percentage with terminator on the correct side per the
 * `phase` label. Independent of which font/emoji set the OS provides.
 *
 * Convention (northern hemisphere):
 *   - waxing phases (new → full): lit portion grows on the RIGHT
 *   - waning phases (full → new): lit portion shrinks from the LEFT
 *
 * Props:
 *   - phase: one of the 8 lunar keys (new, waxing-crescent, first-quarter,
 *            waxing-gibbous, full, waning-gibbous, last-quarter,
 *            waning-crescent)
 *   - illumination: 0..1 (server-provided fractional lit area)
 *   - size: px diameter (default 96)
 *   - color: stroke + lit-fill colour (default currentColor; works with
 *            tailwind text-stamp-red etc.)
 *
 * Iter 31.224 — Six requested precision SVG over emoji rendering because
 * `font-heading` + `text-stamp-red` was forcing mono-fallback on the 7xl
 * waning-gibbous TONIGHT graphic, making it read as a wedge bite. SVG is
 * font-independent and the terminator ellipse encodes illumination exactly.
 */
import React from "react";


function isWaxing(phase) {
    return phase === "waxing-crescent"
        || phase === "first-quarter"
        || phase === "waxing-gibbous";
}

function isWaning(phase) {
    return phase === "waning-gibbous"
        || phase === "last-quarter"
        || phase === "waning-crescent";
}


export default function MoonDisc({
    phase = "full",
    illumination = 0,
    size = 96,
    color = "currentColor",
    "data-testid": testid = "moon-disc",
}) {
    const r = 50;                  // viewBox radius (100×100)
    const cx = 50;
    const cy = 50;
    const strokeWidth = 3;

    // Clamp illum to [0,1]
    const k = Math.max(0, Math.min(1, illumination));

    // ===== Special cases =====
    if (phase === "new") {
        // Dark disc only — outline + filled with the surrounding sky tone.
        // We render as an outlined empty disc (sky shows through), filled
        // with `color` at low alpha to read as a *dark* moon.
        return (
            <svg
                viewBox="0 0 100 100"
                width={size}
                height={size}
                data-testid={testid}
                aria-label={`Moon phase: ${phase}, ${Math.round(k * 100)}% illuminated`}
            >
                <circle
                    cx={cx} cy={cy} r={r - strokeWidth / 2}
                    fill={color}
                    fillOpacity="0.85"
                    stroke={color}
                    strokeWidth={strokeWidth}
                />
            </svg>
        );
    }

    if (phase === "full" || k >= 0.995) {
        // Full disc outline only — bright edge against night sky.
        return (
            <svg
                viewBox="0 0 100 100"
                width={size}
                height={size}
                data-testid={testid}
                aria-label={`Moon phase: ${phase}, ${Math.round(k * 100)}% illuminated`}
            >
                <circle
                    cx={cx} cy={cy} r={r - strokeWidth / 2}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                />
            </svg>
        );
    }

    // ===== General case — lit area = disc with elliptical terminator =====
    //
    // The terminator is the projection of a circle onto the disc's diameter.
    // Its horizontal radius rx = r * |cos(angle)|; the angle maps from k:
    //
    //   k = 0.5 → angle = 0  → rx = r  → straight diameter (half moon)
    //   k = 0   → angle = π/2 → rx = 0  (degenerate, new)
    //   k = 1   → angle = π/2 → rx = 0  (full)
    //
    // For waxing/waning crescent (k < 0.5), the lit area is a thin sliver:
    //   the terminator ellipse SUBTRACTS from the unlit half.
    // For waxing/waning gibbous (k > 0.5), the lit area covers most of the
    //   disc and the terminator ellipse ADDS to the lit half.
    //
    // We draw two overlapping shapes:
    //   1. A half-disc on the lit side (filled).
    //   2. A half-ellipse along the diameter; gibbous = add (filled lit),
    //      crescent = subtract (filled dark = sky color, i.e. fill="none").
    //
    // Simpler approach: use an SVG path with two arcs.

    const litOnRight = isWaxing(phase);   // northern-hemisphere convention
    const litOnLeft = isWaning(phase);
    if (!litOnRight && !litOnLeft) {
        // Shouldn't happen — fall back to a half-lit disc.
        return null;
    }

    // Horizontal radius of the terminator ellipse.
    const rx = r * Math.abs(1 - 2 * k);
    const gibbous = k > 0.5;

    // Build a single path that traces the lit area:
    //   Start at top (0, -r) → arc down the lit side to bottom (0, +r)
    //   → arc back via the terminator ellipse to start.
    //
    // The two arcs differ in their `sweep-flag` depending on whether the
    // terminator bulges into the lit area (crescent) or away from it
    // (gibbous), and on which side is lit.

    // Disc-side arc (the outer rim):
    //   waxing → arc on the right half: sweep=1 (clockwise from top to bottom)
    //   waning → arc on the left half:  sweep=0 (counterclockwise)
    const discSweep = litOnRight ? 1 : 0;

    // Terminator arc (the curved inner edge):
    //   gibbous (k>0.5): bulges outward toward the dark side, so on the lit
    //     side it appears concave from the dark side. The sweep is OPPOSITE
    //     to the disc arc.
    //   crescent (k<0.5): bulges into the lit area, sweep same as disc arc.
    const termSweep = gibbous ? (litOnRight ? 0 : 1) : (litOnRight ? 1 : 0);

    // Path: M top → A r,r (large=0, sweep=discSweep) to bottom → A rx,r
    // (large=0, sweep=termSweep) back to top → Z
    const discRadius = r - strokeWidth / 2;
    const topY = cy - discRadius;
    const botY = cy + discRadius;

    const d = [
        `M ${cx} ${topY}`,
        `A ${discRadius} ${discRadius} 0 0 ${discSweep} ${cx} ${botY}`,
        `A ${rx} ${discRadius} 0 0 ${termSweep} ${cx} ${topY}`,
        "Z",
    ].join(" ");

    return (
        <svg
            viewBox="0 0 100 100"
            width={size}
            height={size}
            data-testid={testid}
            aria-label={`Moon phase: ${phase}, ${Math.round(k * 100)}% illuminated`}
        >
            {/* Outer disc outline so the dark side is still visible. */}
            <circle
                cx={cx} cy={cy} r={discRadius}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeOpacity="0.35"
            />
            {/* Lit area. */}
            <path
                d={d}
                fill={color}
                stroke={color}
                strokeWidth={strokeWidth}
                strokeLinejoin="round"
            />
        </svg>
    );
}
